import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Avatar from '@/components/common/Avatar';
import Breadcrumbs from '@/components/Breadcrumbs';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import useRoutes from '@/hooks/useRoutes';
import { User } from '@/types';
import {
    Edit,
    BookOpen,
    Award,
    CheckCircle,
    FileText,
    Mail,
    TrendingUp,
    LogIn,
} from 'lucide-react';

interface CourseEnrollmentData {
    id: number;
    course: any;
    starts_at: string | null;
    expires_at: string | null;
    total_classes: number;
    completed_classes: number;
    progress_percentage: number;
}

interface CompletedClass {
    id: number;
    class_title: string;
    completed_at: string;
}

interface EvaluationAttempt {
    id: number;
    evaluation_title: string;
    score: number;
    passed: boolean;
    completed_at: string | null;
}

interface AssignmentSubmission {
    id: number;
    assignment_title: string;
    status: string;
    teacher_comment: string;
    reviewed_at: string | null;
    reviewer_name: string | null;
}

interface CredentialsEmail {
    id: number;
    subject: string;
    view_url: string;
    created_at: string;
}

interface EmailData {
    id: number;
    subject: string;
    view_url: string;
    created_at: string;
}

interface LoginHistoryRecord {
    id: number;
    timestamp: string;
    ip_address: string;
    user_agent: string;
}

interface ClassViewRecord {
    id: number;
    class_title: string;
    viewed_at: string;
}

interface DetailProps {
    student: User;
    enrollments: CourseEnrollmentData[];
    completed_classes: CompletedClass[];
    certificates: any[];
    evaluation_attempts: EvaluationAttempt[];
    assignment_submissions: AssignmentSubmission[];
    credentials_email: CredentialsEmail | null;
    all_emails: EmailData[];
    login_history: LoginHistoryRecord[];
    class_views: ClassViewRecord[];
}

const DetailContent: React.FC<DetailProps> = ({
    student,
    enrollments,
    completed_classes,
    certificates,
    evaluation_attempts,
    assignment_submissions,
    credentials_email,
    all_emails,
    login_history,
    class_views,
}) => {
    const { route } = useRoutes();
    const [activeTab, setActiveTab] = useState<string>('enrollments');

    const tabs = [
        { id: 'enrollments', label: 'Inscripciones', icon: BookOpen },
        { id: 'classes', label: 'Clases Completadas', icon: CheckCircle },
        { id: 'certificates', label: 'Certificados', icon: Award },
        { id: 'evaluations', label: 'Evaluaciones', icon: TrendingUp },
        { id: 'assignments', label: 'Tareas', icon: FileText },
        { id: 'emails', label: 'Emails', icon: Mail },
        { id: 'login_history', label: 'Historial de Inicio de Sesión', icon: LogIn },
        { id: 'class_views', label: 'Historial de Clases Vistas', icon: BookOpen },
    ];

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                {/* Breadcrumbs */}
                <Breadcrumbs
                    items={[
                        {
                            label: 'Estudiantes',
                            href: route('staff-students').toString(),
                        },
                        {
                            label: student.full_name,
                        },
                    ]}
                    className="mb-6"
                />

                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                            <Avatar user={student} size="xl" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">
                                    {student.full_name}
                                </h1>
                                <p className="mt-1 text-gray-600">
                                    {student.email}
                                </p>
                                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                                    <span>
                                        Academia: {student.current_academy?.name || '-'}
                                    </span>
                                    <span>Puntos: {student.points || 0}</span>
                                    <span>
                                        Rango: {student.rank?.name || '-'}
                                    </span>
                                    {student.is_active ? (
                                        <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                                            Activo
                                        </span>
                                    ) : (
                                        <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                                            Inactivo
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex space-x-3">
                            <Link
                                href={route('staff-students-edit', {
                                    student_id: student.id,
                                }).toString()}
                            >
                                <PrimaryButton>
                                    <Edit size={16} className="mr-2" />
                                    Editar
                                </PrimaryButton>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center border-b-2 px-1 py-4 text-sm font-medium ${
                                        activeTab === tab.id
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                    }`}
                                >
                                    <Icon size={16} className="mr-2" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="rounded-lg bg-white p-6 shadow">
                    {/* Enrollments Tab */}
                    {activeTab === 'enrollments' && (
                        <div>
                            {enrollments.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay inscripciones
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {enrollments.map((enrollment) => (
                                        <div
                                            key={enrollment.id}
                                            className="rounded-lg border border-gray-200 p-4"
                                        >
                                            <div className="mb-3 flex items-start justify-between">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900">
                                                        {enrollment.course.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-600">
                                                        {enrollment.course.academy?.name}
                                                    </p>
                                                </div>
                                                <div className="text-right text-sm text-gray-500">
                                                    <div>
                                                        Inicio:{' '}
                                                        {formatDate(enrollment.starts_at)}
                                                    </div>
                                                    {enrollment.expires_at && (
                                                        <div>
                                                            Expira:{' '}
                                                            {formatDate(
                                                                enrollment.expires_at,
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mb-2">
                                                <div className="mb-1 flex justify-between text-sm">
                                                    <span className="text-gray-600">
                                                        Progreso
                                                    </span>
                                                    <span className="font-medium text-gray-900">
                                                        {enrollment.completed_classes} /{' '}
                                                        {enrollment.total_classes} clases
                                                        ({enrollment.progress_percentage}%)
                                                    </span>
                                                </div>
                                                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                                    <div
                                                        className="h-full bg-blue-600"
                                                        style={{
                                                            width: `${enrollment.progress_percentage}%`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Completed Classes Tab */}
                    {activeTab === 'classes' && (
                        <div>
                            {completed_classes.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay clases completadas
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Clase
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha de Completación
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {completed_classes.map((cc) => (
                                                <tr key={cc.id}>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                                        {cc.class_title}
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                        {formatDateTime(cc.completed_at)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Certificates Tab */}
                    {activeTab === 'certificates' && (
                        <div>
                            {certificates.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay certificados
                                </p>
                            ) : (
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {certificates.map((cert) => (
                                        <div
                                            key={cert.id}
                                            className="rounded-lg border border-gray-200 p-4"
                                        >
                                            {cert.image_url && (
                                                <img
                                                    src={cert.image_url}
                                                    alt={cert.evaluation?.title}
                                                    className="mb-3 w-full rounded"
                                                />
                                            )}
                                            <h4 className="font-semibold text-gray-900">
                                                {cert.evaluation?.title}
                                            </h4>
                                            <p className="text-sm text-gray-500">
                                                {cert.course?.title}
                                            </p>
                                            <p className="mt-2 text-xs text-gray-400">
                                                Emitido:{' '}
                                                {cert.issued_at ? formatDate(cert.issued_at) : 'N/A'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Evaluations Tab */}
                    {activeTab === 'evaluations' && (
                        <div>
                            {evaluation_attempts.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay intentos de evaluación
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Evaluación
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Puntaje
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Estado
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {evaluation_attempts.map((attempt) => (
                                                <tr key={attempt.id}>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                                        {attempt.evaluation_title}
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                                        {attempt.score.toFixed(1)}%
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        {attempt.passed ? (
                                                            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                                                                Aprobado
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                                                                Reprobado
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                        {formatDateTime(
                                                            attempt.completed_at,
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Assignments Tab */}
                    {activeTab === 'assignments' && (
                        <div>
                            {assignment_submissions.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay entregas de tareas
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {assignment_submissions.map((submission) => (
                                        <div
                                            key={submission.id}
                                            className="rounded-lg border border-gray-200 p-4"
                                        >
                                            <div className="mb-2 flex items-start justify-between">
                                                <h4 className="font-semibold text-gray-900">
                                                    {submission.assignment_title}
                                                </h4>
                                                <span
                                                    className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                                        submission.status ===
                                                        'approved'
                                                            ? 'bg-green-100 text-green-800'
                                                            : submission.status ===
                                                                'rejected'
                                                              ? 'bg-red-100 text-red-800'
                                                              : 'bg-yellow-100 text-yellow-800'
                                                    }`}
                                                >
                                                    {submission.status === 'approved'
                                                        ? 'Aprobado'
                                                        : submission.status ===
                                                            'rejected'
                                                          ? 'Rechazado'
                                                          : 'Pendiente'}
                                                </span>
                                            </div>
                                            {submission.teacher_comment && (
                                                <div className="mb-2">
                                                    <p className="text-sm text-gray-600">
                                                        <strong>Comentario:</strong>{' '}
                                                        {submission.teacher_comment}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>
                                                    Revisado:{' '}
                                                    {formatDateTime(
                                                        submission.reviewed_at,
                                                    )}
                                                </span>
                                                {submission.reviewer_name && (
                                                    <span>
                                                        Por: {submission.reviewer_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'emails' && (
                        <div>
                            {all_emails.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No se han enviado emails a este estudiante
                                </p>
                            ) : (
                                <div className="overflow-hidden rounded-lg bg-white shadow">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Asunto
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Acciones
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {all_emails.map((email) => (
                                                <tr
                                                    key={email.id}
                                                    className={
                                                        credentials_email?.id === email.id
                                                            ? 'bg-blue-50'
                                                            : ''
                                                    }
                                                >
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="flex items-center">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {email.subject}
                                                                {credentials_email?.id === email.id && (
                                                                    <span className="ml-2 inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                                                                        Credenciales
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="text-sm text-gray-500">
                                                            {formatDateTime(email.created_at)}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                                        <a
                                                            href={email.view_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center text-blue-600 hover:text-blue-900"
                                                        >
                                                            <Mail size={16} className="mr-1" />
                                                            Ver Email
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'login_history' && (
                        <div>
                            {login_history.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay registros de inicio de sesión
                                </p>
                            ) : (
                                <div className="overflow-hidden rounded-lg bg-white shadow">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha y Hora
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Dirección IP
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Navegador/Dispositivo
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {login_history.map((login) => (
                                                <tr key={login.id}>
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="text-sm text-gray-900">
                                                            {formatDateTime(login.timestamp)}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="text-sm text-gray-500">
                                                            {login.ip_address}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-500">
                                                            {login.user_agent.length > 100
                                                                ? `${login.user_agent.substring(0, 100)}...`
                                                                : login.user_agent}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'class_views' && (
                        <div>
                            {class_views.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay historial de clases vistas
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Clase
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha de visualización
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {class_views.map((classView) => (
                                                <tr key={classView.id}>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                                        {classView.class_title}
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                        {formatDateTime(classView.viewed_at)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </StaffLayout>
    );
};

const Detail: React.FC<DetailProps> = (props) => {
    return (
        <ThemeWrapper>
            <DetailContent {...props} />
        </ThemeWrapper>
    );
};

export default Detail;
